/**
 * Compact staff-only comment at the bottom of a remote fiche details tab.
 * The saved text comes from the user already loaded with the fiche.
 * Edit, save, cancel, and delete are icon buttons.
 */
import * as React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { MessageSquare } from 'lucide-react';
import { Button } from '@/components/forms/Button';
import { Textarea } from '@/components/forms/Textarea';
import { toast } from '@/components/ui/toast';
import { updateUserProfile } from '@/services/users.service';
import { getErrorMessage } from '@/utils/errors-utils';

const MAX_LENGTH = 4000;

type ListCache = {
  data?: Array<{
    userId?: string;
    user?: { internalComment?: string | null } | null;
  }>;
};

function rememberComment(
  queryClient: ReturnType<typeof useQueryClient>,
  userId: string,
  comment: string | null,
) {
  queryClient.setQueriesData<ListCache>({ queryKey: ['onboarding'] }, (current) => {
    if (!current || !Array.isArray(current.data)) return current;
    return {
      ...current,
      data: current.data.map((row) => {
        if (row.userId !== userId || !row.user) return row;
        return { ...row, user: { ...row.user, internalComment: comment } };
      }),
    };
  });
}

/** Props for {@link InternalCommentBox}. */
type InternalCommentBoxProps = Readonly<{
  userId: string;
  comment: string | null;
}>;

/** Compact internal-comment card for a details tab. */
export function InternalCommentBox({ userId, comment }: InternalCommentBoxProps) {
  const queryClient = useQueryClient();
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState('');
  const [saved, setSaved] = React.useState(comment);

  React.useEffect(() => {
    setSaved(comment);
    setEditing(false);
  }, [userId, comment]);

  React.useEffect(() => {
    if (editing) textareaRef.current?.focus();
  }, [editing]);

  const save = useMutation({
    mutationFn: (next: string) => updateUserProfile(userId, { internalComment: next }),
    onSuccess: (_data, next) => {
      setSaved(next);
      rememberComment(queryClient, userId, next);
      setEditing(false);
      toast.success('Commentaire interne enregistré');
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  const remove = useMutation({
    mutationFn: () => updateUserProfile(userId, { internalComment: null }),
    onSuccess: () => {
      setSaved(null);
      rememberComment(queryClient, userId, null);
      setDraft('');
      setEditing(false);
      toast.success('Commentaire interne supprimé');
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  const pending = save.isPending || remove.isPending;
  const trimmed = draft.trim();

  const openEditor = () => {
    setDraft(saved ?? '');
    setEditing(true);
  };

  const closeEditor = () => {
    setDraft(saved ?? '');
    setEditing(false);
  };

  return (
    <div className="section-card-full rounded-xl border border-[color-mix(in_srgb,var(--color-1-nuance-3)_30%,transparent)] bg-[var(--card)] p-3">
      <div className="flex items-center gap-2">
        <MessageSquare className="w-4 h-4 shrink-0 text-[var(--color-1-nuance-1)]" aria-hidden />
        <h4 className="text-sm font-semibold text-[var(--color-1-nuance-1)]">
          Commentaire interne
        </h4>
        <div className="ml-auto flex items-center gap-1">
          {!editing && (
            <Button
              type="button"
              preset="row-edit"
              tooltipText={saved ? 'Modifier' : 'Ajouter'}
              aria-label={
                saved ? 'Modifier le commentaire interne' : 'Ajouter un commentaire interne'
              }
              onClick={openEditor}
            />
          )}
          {editing && (
            <>
              <Button
                type="button"
                preset="row-validate"
                tooltipText="Valider"
                aria-label="Valider le commentaire interne"
                loading={save.isPending}
                disabled={pending || trimmed.length === 0}
                onClick={() => save.mutate(trimmed)}
              />
              {saved && (
                <Button
                  type="button"
                  preset="row-delete"
                  tooltipText="Supprimer"
                  aria-label="Supprimer le commentaire interne"
                  loading={remove.isPending}
                  disabled={pending}
                  onClick={() => remove.mutate()}
                />
              )}
              <Button
                type="button"
                preset="row-cancel"
                tooltipText="Annuler"
                aria-label="Annuler la modification"
                disabled={pending}
                onClick={closeEditor}
              />
            </>
          )}
        </div>
      </div>

      {!editing && (
        <p className="mt-2 text-sm text-foreground whitespace-pre-wrap">
          {saved ?? <span className="text-muted-foreground">Aucun commentaire interne.</span>}
        </p>
      )}

      {editing && (
        <Textarea
          ref={textareaRef}
          id="internal-comment"
          aria-label="Commentaire interne"
          containerClassName="mt-2"
          className="min-h-16 py-1.5"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          maxLength={MAX_LENGTH}
          rows={2}
          placeholder="Suivi réservé aux équipes internes"
          disabled={pending}
        />
      )}
    </div>
  );
}
